import type { TracesReadUserTracesData } from "@common/client"
import {
  TraceAdEvalReportsService,
  TraceSbEvalReportsService,
  TraceService,
  TracesService,
} from "@common/client"
import type { QueryClient } from "@tanstack/react-query"
import { keepPreviousData } from "@tanstack/react-query"
import {
  makeAxiosMutation,
  makeInfiniteQuery,
  makeNoArgsQuery,
  makeQuery,
} from "./utils"

export const tracesBaseKey = ["traces"] as const
export const traceBaseKey = ["trace"] as const

export function invalidateTracesList(
  queryClient: QueryClient,
  options?: { refetchType?: "active" | "inactive" | "all" | "none" },
) {
  const refetchType = options?.refetchType ?? "none"
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: tracesBaseKey, refetchType }),
    queryClient.invalidateQueries({ queryKey: traceBaseKey, refetchType }),
  ])
}

export const tracesKey = (
  params: NonNullable<TracesReadUserTracesData["query"]>,
) =>
  [
    ...tracesBaseKey,
    params.skip,
    params.limit,
    params.order_by,
    params.order_direction,
    params.search,
    params.scope,
  ] as const
export const traceKey = (id: string) => [...traceBaseKey, id] as const

export const evaluationReportKey = (reportId: string) =>
  ["evaluation-report", reportId] as const

export const traceQuery = makeQuery({
  serviceCall: TracesService.readItem,
  queryKey: (params) => traceKey(params.id),
})

export const traceListQuery = makeQuery({
  serviceCall: TracesService.readUserTraces,
  queryKey: tracesKey,
  placeholderData: keepPreviousData,
})

export const tracesInfiniteQuery = makeInfiniteQuery({
  serviceCall: (
    params: Omit<
      NonNullable<TracesReadUserTracesData["query"]>,
      "skip" | "limit"
    >,
    page,
  ) => TracesService.readUserTraces({ ...params, ...page }),
  queryKey: (params) => [...tracesBaseKey, "infinite", params],
  pageSize: 25,
})

export const evaluationReportQuery = makeQuery({
  serviceCall: TraceAdEvalReportsService.readItem,
  queryKey: (params) => evaluationReportKey(params.id),
})

export const traceAudioAvailableIntentsQuery = makeNoArgsQuery({
  serviceCall: TraceService.availableIntentsV2,
  queryKey: [tracesBaseKey],
})

export const useDeleteTraceMutation = makeAxiosMutation({
  serviceCall: TracesService.deleteItem,
  invalidates: () => [tracesBaseKey],
})

export const useUpdateTraceMutation = makeAxiosMutation({
  serviceCall: TracesService.updateItem,
  invalidates: (params) => [tracesBaseKey, traceKey(params.id)],
})

export const useArchiveTraceMutation = makeAxiosMutation({
  serviceCall: TracesService.archiveItem,
  invalidates: (params) => [tracesBaseKey, traceKey(params.id)],
})

export const useRestoreTraceMutation = makeAxiosMutation({
  serviceCall: TracesService.restoreItem,
  invalidates: (params) => [tracesBaseKey, traceKey(params.id)],
})

export const useTraceAudioMutation = makeAxiosMutation({
  serviceCall: TraceService.audio,
  invalidates: () => [],
})

export const useTranscriptionMutation = makeAxiosMutation({
  serviceCall: TraceService.transcribe,
  invalidates: () => [],
})

export const useUpdateEvaluationFieldMutation = makeAxiosMutation({
  serviceCall: TraceAdEvalReportsService.updateField,
  scope: { id: "evaluation-field" },
  invalidates: (p) => [evaluationReportKey(p.report_id)],
})

export const useFinalizeEvaluationFieldMutation = makeAxiosMutation({
  serviceCall: TraceAdEvalReportsService.finalizeField,
  scope: { id: "evaluation-field" },
  invalidates: (p) => [evaluationReportKey(p.report_id)],
})

export const useUnfinalizeEvaluationFieldMutation = makeAxiosMutation({
  serviceCall: TraceAdEvalReportsService.unfinalizeField,
  scope: { id: "evaluation-field" },
  invalidates: (p) => [evaluationReportKey(p.report_id)],
})

export const sbEvaluationReportKey = (reportId: string) =>
  ["sb-evaluation-report", reportId] as const

export const sbEvaluationReportQuery = makeQuery({
  serviceCall: TraceSbEvalReportsService.readItem,
  queryKey: (params) => sbEvaluationReportKey(params.id),
})

export const useUpdateSbEvaluationFieldMutation = makeAxiosMutation({
  serviceCall: TraceSbEvalReportsService.updateField,
  scope: { id: "evaluation-field" },
  invalidates: (p) => [sbEvaluationReportKey(p.report_id)],
})

export const useFinalizeSbEvaluationFieldMutation = makeAxiosMutation({
  serviceCall: TraceSbEvalReportsService.finalizeField,
  scope: { id: "evaluation-field" },
  invalidates: (p) => [sbEvaluationReportKey(p.report_id)],
})

export const useUnfinalizeSbEvaluationFieldMutation = makeAxiosMutation({
  serviceCall: TraceSbEvalReportsService.unfinalizeField,
  scope: { id: "evaluation-field" },
  invalidates: (p) => [sbEvaluationReportKey(p.report_id)],
})
